import { PresentationStatus } from "../interfaces/IPresentationStatus";
import { alpha, keyframes, Theme } from "@mui/material";
import { SystemStyleObject } from "@mui/system";

export type ScheduleStatus = "PLANNED" | "IN_PROGRESS" | "FINISHED";

export const getPulseAnimation = (theme: Theme) => {
  const color = theme.palette.info.main;
  const r = parseInt(color.slice(1, 3), 16);
  const g = parseInt(color.slice(3, 5), 16);
  const b = parseInt(color.slice(5, 7), 16);

  return keyframes`
    0%, 100% {
      background-color: transparent;
    }
    50% {
      background-color: rgba(${r}, ${g}, ${b}, 0.15);
    }
  `;
};

export const getStatusColor = (
  status: ScheduleStatus | PresentationStatus,
  theme: Theme,
): string => {
  if (status === "IN_PROGRESS" || status === PresentationStatus.DANCING) {
    return theme.palette.info.main;
  }
  if (status === "FINISHED" || status === PresentationStatus.DONE) {
    return theme.palette.success.main;
  }
  return theme.palette.text.primary;
};

export const getActiveStatusStyles = (theme: Theme): SystemStyleObject<Theme> => {
  const pulseBackground = getPulseAnimation(theme);
  return {
    animation: `${pulseBackground} 1.8s ease-in-out infinite`,
  };
};

export const getFinishedStatusStyles = (
  theme: Theme,
  includeBackground: boolean = false,
): SystemStyleObject<Theme> => {
  const isDark = theme.palette.mode === "dark";
  const baseStyles: SystemStyleObject<Theme> = {
    opacity: isDark ? 0.6 : 0.65,
    color: isDark
      ? alpha(theme.palette.text.primary, 0.5)
      : alpha(theme.palette.text.primary, 0.6),
    "& .MuiTypography-root": {
      color: isDark
        ? alpha(theme.palette.text.primary, 0.5)
        : alpha(theme.palette.text.primary, 0.6),
    },
  };

  if (includeBackground) {
    const successColor = theme.palette.success.main;
    const r = parseInt(successColor.slice(1, 3), 16);
    const g = parseInt(successColor.slice(3, 5), 16);
    const b = parseInt(successColor.slice(5, 7), 16);
    baseStyles.backgroundColor = `rgba(${r}, ${g}, ${b}, ${isDark ? 0.15 : 0.08})`;
  }

  return baseStyles;
};

export const getPlannedStatusStyles = (): SystemStyleObject<Theme> => ({});

export const getScheduleStatusStyles = (
  status: ScheduleStatus,
  theme: Theme,
  options?: { includeBackground?: boolean },
): SystemStyleObject<Theme> => {
  switch (status) {
    case "IN_PROGRESS":
      return getActiveStatusStyles(theme);
    case "FINISHED":
      return getFinishedStatusStyles(theme, options?.includeBackground);
    case "PLANNED":
    default:
      return getPlannedStatusStyles();
  }
};

export const getPresentationStatusStyles = (
  status: PresentationStatus,
  theme: Theme,
  options?: { includeBackground?: boolean },
): SystemStyleObject<Theme> => {
  switch (status) {
    case PresentationStatus.DANCING:
      return getActiveStatusStyles(theme);
    case PresentationStatus.DONE:
      return getFinishedStatusStyles(theme, options?.includeBackground);
    case PresentationStatus.PLANNED:
    default:
      return getPlannedStatusStyles();
  }
};
